import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Computer,
  Smartphone,
  Info as InfoIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";

import { JsonSyntaxHighlighter, JsonFormatter, JsonModal } from "./JsonViewer";

const CustomerActivityDialog = ({
  open,
  onClose,
  activityData,
  loading = false,
  fetchingData = false, // New prop for the refresh icon
  customerName = "",
  customerEmail = "",
  onPageChange,
  onPageSizeChange,
  onFilterChange,
}) => {
  const {
    activities = [],
    totalCount = 0,
    pageSize = 25,
    page = 0,
  } = activityData || {};

  // Debounce timer for filter changes
  const filterTimeoutRef = useRef(null);

  // Debounced filter handler
  const handleFilterChange = useCallback(
    (filterModel) => {
      if (!onFilterChange) return;

      // Clear previous timeout
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }

      // Check if there are valid filters
      const hasValidFilters =
        filterModel.items &&
        filterModel.items.length > 0 &&
        filterModel.items.some(
          (item) =>
            item.value !== undefined && item.value !== null && item.value !== ""
        );

      // Set timeout - shorter delay for clearing filters, longer for setting them
      filterTimeoutRef.current = setTimeout(
        () => {
          console.log(
            "🔍 Sending filter to server:",
            filterModel,
            "hasValidFilters:",
            hasValidFilters
          );
          // Always pass the filterModel to parent (including empty to clear filters)
          onFilterChange(filterModel);
        },
        hasValidFilters ? 500 : 100
      ); // 100ms for clearing, 500ms for setting filters
    },
    [onFilterChange]
  );

  const columns = [
    {
      field: "responseCode",
      headerName: "Status / Time",
      width: 160,
      filterable: true,
      valueGetter: (params) => params.row.responseCode,
      renderCell: (params) => {
        const dateValue = params.row.logTimestamp
          ? new Date(params.row.logTimestamp).toLocaleString()
          : "-";

        const status = params.row.responseCode || "-";

        const getStatusColor = (status) => {
          const statusCode = parseInt(status);
          if (statusCode >= 200 && statusCode < 300) return "success";
          if (statusCode >= 300 && statusCode < 400) return "info";
          if (statusCode >= 400 && statusCode < 500) return "warning";
          if (statusCode >= 500) return "error";
          return "default";
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            {/* Status code only */}
            <Chip
              label={status}
              size="small"
              color={getStatusColor(status)}
              sx={{
                fontSize: "0.65rem",
                height: "18px",
                minWidth: "35px",
                fontWeight: "bold",
                "& .MuiChip-label": { px: 0.4 },
              }}
            />
            {/* Timestamp below */}
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              {dateValue}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "timeTaken",
      headerName: "Duration",
      width: 100,
      renderCell: (params) => {
        const timeTaken = params.value;

        if (!timeTaken || timeTaken === 0) {
          return (
            <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
              -
            </Typography>
          );
        }

        // Format time taken based on value - returns JSX for different font sizes
        const formatDuration = (milliseconds) => {
          if (milliseconds < 1000) {
            return (
              <span>
                {milliseconds}
                <span style={{ fontSize: "0.5rem" }}> ms</span>
              </span>
            );
          } else if (milliseconds < 60000) {
            return (
              <span>
                {(milliseconds / 1000).toFixed(1)}
                <span style={{ fontSize: "0.5rem" }}> s</span>
              </span>
            );
          } else {
            const minutes = Math.floor(milliseconds / 60000);
            const seconds = Math.floor((milliseconds % 60000) / 1000);
            return (
              <span>
                {minutes}
                <span style={{ fontSize: "0.5rem" }}> m</span> {seconds}
                <span style={{ fontSize: "0.5rem" }}> s</span>
              </span>
            );
          }
        };

        // Color coding based on duration
        const getDurationColor = (ms) => {
          if (ms < 500) return "success.main"; // Fast - green
          if (ms < 2000) return "warning.main"; // Medium - orange
          return "error.main"; // Slow - red
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: getDurationColor(timeTaken),
              }}
            >
              {formatDuration(timeTaken)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "path",
      headerName: "Method / Path",
      width: 260,
      filterable: true,
      valueGetter: (params) => params.row.path,
      renderCell: (params) => {
        const method = params.row.method || "-";
        const path = params.row.path || "-";
        const clientType = params.row.clientType || "";
        const buildVersion = params.row.buildVersion || "";
        const platform = params.row.platform || "";
        const browser = params.row.browser || "";

        // Helper function to get platform/browser chip color
        const getPlatformChipColor = (platformName) => {
          const name = platformName.toLowerCase();
          if (name.includes("ios")) return "info";
          if (name.includes("android")) return "success";
          if (name.includes("chrome")) return "warning";
          if (name.includes("firefox")) return "error";
          if (name.includes("safari")) return "info";
          if (name.includes("edge")) return "primary";
          return "default";
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {/* First line: API Path only */}
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontFamily: "monospace",
                color: "text.primary",
                wordBreak: "break-all",
              }}
            >
              {path}
            </Typography>

            {/* Second line: Method chip first, then platform/browser info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexWrap: "wrap",
              }}
            >
              {/* HTTP Method chip as first element */}
              <Chip
                label={method}
                size="small"
                color={
                  method === "GET"
                    ? "primary"
                    : method === "POST"
                      ? "success"
                      : method === "PUT"
                        ? "warning"
                        : method === "DELETE"
                          ? "error"
                          : "default"
                }
                sx={{
                  minWidth: "45px",
                  fontSize: "0.65rem",
                  height: "18px",
                  "& .MuiChip-label": { px: 0.4 },
                }}
              />

              {/* Client type icon */}
              {clientType === "mobile" ? (
                <Smartphone
                  sx={{ fontSize: "14px", color: "text.secondary" }}
                />
              ) : clientType === "web" ? (
                <Computer sx={{ fontSize: "14px", color: "text.secondary" }} />
              ) : null}

              {/* Platform chip for mobile (iOS, Android) */}
              {clientType === "mobile" && platform && (
                <Chip
                  label={platform}
                  size="small"
                  variant="outlined"
                  color={getPlatformChipColor(platform)}
                  sx={{
                    fontSize: "0.6rem",
                    height: "18px",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              )}

              {/* Browser chip for web */}
              {clientType === "web" && browser && (
                <Chip
                  label={browser}
                  size="small"
                  variant="outlined"
                  color={getPlatformChipColor(browser)}
                  sx={{
                    fontSize: "0.6rem",
                    height: "18px",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              )}

              {/* Build version chip */}
              {buildVersion && (
                <Chip
                  label={`v${buildVersion}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.6rem",
                    height: "18px",
                    color: "text.secondary",
                    borderColor: "text.secondary",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              )}

              {/* Fallback for generic client type */}
              {!platform && !browser && clientType && (
                <Chip
                  label={clientType}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.6rem",
                    height: "18px",
                    color: "text.secondary",
                    borderColor: "text.secondary",
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "request",
      headerName: "Request",
      width: 300,
      renderCell: (params) => {
        // Check for request data in multiple possible fields
        const requestData = params.value || params.row.params;

        if (!requestData || requestData === "{}") {
          return <span>-</span>;
        }

        return (
          <JsonModal data={requestData}>
            <Box
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <JsonFormatter data={requestData} maxWidth={280} compact={true} />
              <InfoIcon
                sx={{ ml: 0.5, fontSize: 16, color: "text.secondary" }}
              />
            </Box>
          </JsonModal>
        );
      },
    },
    {
      field: "response",
      headerName: "Response",
      width: 500,
      renderCell: (params) => {
        const responseData = params.value;

        if (!responseData || responseData === "{}") {
          return <span>-</span>;
        }

        return (
          <JsonModal data={responseData}>
            <Box
              sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <JsonFormatter
                data={responseData}
                maxWidth={450}
                compact={true}
              />
              <InfoIcon
                sx={{ ml: 0.5, fontSize: 16, color: "text.secondary" }}
              />
            </Box>
          </JsonModal>
        );
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "95vw",
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AssessmentIcon sx={{ color: "white" }} />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ color: "white" }}>
              Customer Activity - {customerName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                component="small"
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {customerEmail && `Email: ${customerEmail}`}
              </Typography>
              {fetchingData && (
                <RefreshIcon
                  sx={{
                    fontSize: 16,
                    color: "white",
                    animation: "spin 1s linear infinite",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <div>Loading customer activity...</div>
          </Box>
        ) : (
          <div style={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={activities.map((activity, index) => ({
                id: activity.id || index,
                ...activity,
              }))}
              columns={columns}
              loading={loading}
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              paginationMode="server"
              disableColumnFilter={false}
              disableColumnMenu={false}
              filterMode="server"
              onFilterModelChange={handleFilterChange}
              onPageChange={(newPage, details) => {
                if (onPageChange) onPageChange(newPage);
              }}
              onPageSizeChange={(newPageSize, details) => {
                if (onPageSizeChange) onPageSizeChange(newPageSize);
              }}
              hideFooter={false}
              disableSelectionOnClick={true}
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  lineHeight: "normal",
                  padding: "8px",
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "none !important",
                },
              }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomerActivityDialog;
